import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import type { ReleasePost } from 'fym-shared/src/music24/releases';
import { TypedMemo } from '../../../../common/types';
import { Panel } from '../../../components/primitives/Panel';
import { PlanPostState, planPostState } from '../plan/planModel';
import { POST_STATE_MESSAGES, postPlatformName, postStepLine, releasePostName } from '../plan/planPostLabels';
import { planPostTime } from '../plan/rolloutDayCopy';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Overview.module.scss';
/* eslint-enable css-modules/no-unused-class */

const STATE_CLASS: Record<PlanPostState, string | undefined> = {
  posted: styles.stateOk,
  scheduled: styles.stateScheduled,
  publishing: styles.stateScheduled,
  draft: styles.stateDraft,
  needsYou: styles.stateWarn,
  failed: styles.stateBad,
};

interface OverviewPostRowProps {
  post: ReleasePost;
  /** The link at the end of the row ("View posts"); absent: none. */
  action?: { label: string; onClick: () => void };
}

/** "Countdown 7 days · Story", the time, the platform and the state (design b1 `.prow`). */
const OverviewPostRowComponent: React.FC<OverviewPostRowProps> = ({ post, action }) => {
  const intl = useIntl();
  const state = planPostState(post.status);
  const title = postStepLine(intl, post, releasePostName(intl, post));

  return (
    <Panel nested className={styles.postRow} data-testid="overview-post-row">
      <span className={styles.postText}>
        <b className={styles.postTitle}>{title}</b>
        <small className={styles.postWhen}>{planPostTime(intl, post.scheduledAt)}</small>
      </span>
      <span className={styles.platforms}>{postPlatformName(post.platform)}</span>
      <span className={clsx(styles.state, STATE_CLASS[state])}>{intl.formatMessage(POST_STATE_MESSAGES[state])}</span>
      {action !== undefined && (
        <button type="button" className={styles.textLink} onClick={action.onClick}>
          {action.label}
        </button>
      )}
    </Panel>
  );
};

export const OverviewPostRow = TypedMemo(OverviewPostRowComponent);
